import Navbar from './Navbar';
import Footer from './Footer';

/* ── Shared policy page wrapper ── */
function PolicyLayout({ title, lastUpdated, children }) {
  return (
    <div className="min-h-screen bg-[#F3F4F6] font-['Manrope']">
      <Navbar />
      <div className="pt-20 sm:pt-24">
        {/* Hero strip */}
        <div className="bg-[#1f3d2b] py-14 sm:py-20 px-4">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-[#d97736] text-xs font-bold uppercase tracking-[0.25em] mb-3 mt-4">
              Legal
            </p>
            <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-semibold text-white">
              {title}
            </h1>
            <p className="text-white/50 text-sm mt-4">Last updated: {lastUpdated}</p>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 sm:p-12 prose prose-gray max-w-none policy-content">
            {children}
          </div>
          <div className="mt-8 text-center text-sm text-gray-500">
            Questions? Email us at{' '}
            <a href="mailto:info@montclairvitamins.com" className="text-[#1f3d2b] font-semibold hover:underline">
              info@montclairvitamins.com
            </a>{' '}
            or call{' '}
            <a href="tel:5103380667" className="text-[#1f3d2b] font-semibold hover:underline">
              (510) 338-0667
            </a>
          </div>
        </div>
      </div>
      <Footer />

      <style>{`
        .policy-content h2 {
          font-family: 'Cormorant Garamond', serif;
          font-size: 1.6rem;
          font-weight: 600;
          color: #1f3d2b;
          margin-top: 2.5rem;
          margin-bottom: 0.75rem;
          padding-bottom: 0.5rem;
          border-bottom: 2px solid #F3F4F6;
        }
        .policy-content h3 {
          font-size: 1rem;
          font-weight: 700;
          color: #1a1a1a;
          margin-top: 1.5rem;
          margin-bottom: 0.5rem;
        }
        .policy-content p {
          color: #4b5563;
          line-height: 1.75;
          margin-bottom: 1rem;
          font-size: 0.9375rem;
        }
        .policy-content ul {
          padding-left: 1.5rem;
          margin-bottom: 1rem;
          list-style: disc;
        }
        .policy-content ul li {
          color: #4b5563;
          line-height: 1.75;
          font-size: 0.9375rem;
          margin-bottom: 0.3rem;
        }
        .policy-content a {
          color: #1f3d2b;
          font-weight: 600;
          text-decoration: underline;
        }
        .policy-content strong {
          color: #1a1a1a;
          font-weight: 700;
        }
      `}</style>
    </div>
  );
}

/* ─────────────────────────────────────────
   PRIVACY POLICY
───────────────────────────────────────── */
export function PrivacyPolicyPage() {
  return (
    <PolicyLayout title="Privacy Policy" lastUpdated="February 2026">
      <p>
        Healthy Life Vitamins ("we," "us," or "our") operates{' '}
        <a href="https://www.montclairvitamins.com">www.montclairvitamins.com</a>. This Privacy
        Policy explains how we collect, use, and protect your personal information when you visit
        our website or book a Wellness Suite session.
      </p>

      <h2>1. Information We Collect</h2>
      <h3>Information you provide directly</h3>
      <ul>
        <li>Name, phone number, and email address when booking a session or submitting our health intake form.</li>
        <li>Health and wellness information provided in the Liability & Wellness Intake Form, used solely to ensure your safe use of our equipment.</li>
        <li>Payment information processed securely by <strong>Stripe</strong>. We do not store your card details on our servers.</li>
      </ul>
      <h3>Information collected automatically</h3>
      <ul>
        <li>Browser type, device, IP address, and pages visited for analytics purposes.</li>
        <li>Session cookies necessary for booking functionality.</li>
      </ul>

      <h2>2. How We Use Your Information</h2>
      <ul>
        <li>To process and confirm your Wellness Suite bookings.</li>
        <li>To send booking confirmations and reminder emails via <strong>Resend</strong>.</li>
        <li>To verify your health intake waiver status before allowing equipment use.</li>
        <li>To manage your session credits, pack purchases, and monthly membership.</li>
        <li>To provide personalized supplement and vitamin recommendations upon request.</li>
        <li>To improve our website and services.</li>
      </ul>

      <h2>3. How We Share Your Information</h2>
      <p>We do not sell, rent, or trade your personal information. We share data only with:</p>
      <ul>
        <li><strong>Stripe</strong> — for secure payment processing.</li>
        <li><strong>Resend</strong> — for transactional email delivery (booking confirmations, reminders).</li>
        <li>Law enforcement or legal authorities when required by law.</li>
      </ul>

      <h2>4. Health Information</h2>
      <p>
        Information submitted through our Liability & Wellness Intake Form (including health
        conditions, medications, and physical limitations) is used solely for safety screening
        before your first Wellness Suite session. This information is stored securely and is not
        shared with third parties except as required by law.
      </p>

      <h2>5. Data Retention</h2>
      <p>
        We retain your booking and account information for as long as necessary to provide our
        services and comply with legal obligations. Health intake waivers are retained for a
        minimum of 3 years. You may request deletion of your data by contacting us at{' '}
        <a href="mailto:info@montclairvitamins.com">info@montclairvitamins.com</a>.
      </p>

      <h2>6. Cookies</h2>
      <p>
        Our website uses functional cookies to maintain your booking session. We do not use
        advertising or tracking cookies. You may disable cookies in your browser settings, though
        this may affect booking functionality.
      </p>

      <h2>7. Security</h2>
      <p>
        We use industry-standard security practices to protect your data. Payment transactions are
        encrypted via <strong>TLS/SSL</strong> and processed through Stripe's PCI-compliant
        infrastructure.
      </p>

      <h2>8. Your Rights (California Residents)</h2>
      <p>
        Under the California Consumer Privacy Act (CCPA), California residents have the right to
        know what personal data we collect, request deletion of their data, and opt out of data
        sale (we do not sell data). To exercise these rights, contact us at{' '}
        <a href="mailto:info@montclairvitamins.com">info@montclairvitamins.com</a>.
      </p>

      <h2>9. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy periodically. Changes will be posted on this page with
        an updated "Last updated" date.
      </p>

      <h2>10. Contact Us</h2>
      <p>
        Healthy Life Vitamins · 6130 Medau Pl, Oakland, CA 94611<br />
        <a href="tel:5103380667">(510) 338-0667</a> ·{' '}
        <a href="mailto:info@montclairvitamins.com">info@montclairvitamins.com</a>
      </p>
    </PolicyLayout>
  );
}

/* ─────────────────────────────────────────
   TERMS OF SERVICE
───────────────────────────────────────── */
export function TermsOfServicePage() {
  return (
    <PolicyLayout title="Terms of Service" lastUpdated="February 2026">
      <p>
        By accessing our website or booking a Wellness Suite session at Healthy Life Vitamins
        ("we," "us," "our"), you agree to these Terms of Service. Please read them carefully.
      </p>

      <h2>1. Our Services</h2>
      <p>
        Healthy Life Vitamins offers retail sales of vitamins, supplements, and natural personal
        care products, as well as Wellness Suite sessions at our Montclair Village location
        (6130 Medau Pl, Oakland, CA 94611). Wellness Suite sessions include access to our
        infrared red light therapy pod and 4D deep tissue massage chair in a fully private room.
      </p>

      <h2>2. Eligibility & Health Requirements</h2>
      <p>
        You must be 18 years of age or older to book a Wellness Suite session. All first-time
        visitors are required to complete our <strong>Liability & Wellness Intake Form</strong>{' '}
        before using any equipment. By signing the intake form, you acknowledge:
      </p>
      <ul>
        <li>You do not have contraindicated conditions (including active pacemakers, uncontrolled high blood pressure, seizure disorders, or pregnancy).</li>
        <li>You have removed all jewelry (rings, bracelets, earrings) before using equipment.</li>
        <li>You are voluntarily participating and assume responsibility for your experience.</li>
      </ul>

      <h2>3. Booking & Payments</h2>
      <ul>
        <li>Bookings are confirmed only after successful payment via <strong>Stripe</strong>.</li>
        <li>Session packs (2-pack: $125) are valid for <strong>30 days</strong> from the date of purchase.</li>
        <li>Monthly memberships ($220/month) automatically renew each month until cancelled.</li>
        <li>Credit card information is processed and stored securely by Stripe. We do not store card details.</li>
      </ul>

      <h2>4. Membership Terms</h2>
      <ul>
        <li>Monthly memberships include 4 sessions per month, priority booking, and all Wellness Club Perks.</li>
        <li>Unused sessions within a billing period do not roll over to subsequent months.</li>
        <li>You may cancel your membership at any time; cancellation takes effect at the end of the current billing period.</li>
        <li>Wellness Club Perks (pre-order pick-up, personalized vitamin plan, 5% cashback, 10% off Wednesdays) are exclusive to active monthly members.</li>
      </ul>

      <h2>5. Assumption of Risk</h2>
      <p>
        Wellness Suite equipment (infrared therapy, massage chair) involves physical activity and
        therapy. You voluntarily assume all risks associated with the use of this equipment.
        Healthy Life Vitamins is not liable for injuries arising from failure to disclose
        contraindicated health conditions or failure to follow staff guidance.
      </p>

      <h2>6. Intellectual Property</h2>
      <p>
        All website content, branding, images, and copy are the property of Healthy Life Vitamins.
        You may not reproduce, distribute, or use our content without written permission.
      </p>

      <h2>7. Limitation of Liability</h2>
      <p>
        To the maximum extent permitted by law, Healthy Life Vitamins shall not be liable for any
        indirect, incidental, or consequential damages arising from your use of our services. Our
        liability is limited to the amount paid for the specific service giving rise to the claim.
      </p>

      <h2>8. Governing Law</h2>
      <p>
        These Terms are governed by the laws of the State of California. Any disputes shall be
        resolved in the courts of Alameda County, California.
      </p>

      <h2>9. Changes to Terms</h2>
      <p>
        We reserve the right to modify these Terms at any time. Continued use of our website or
        services after changes constitutes acceptance of the new Terms.
      </p>

      <h2>10. Contact</h2>
      <p>
        Healthy Life Vitamins · 6130 Medau Pl, Oakland, CA 94611<br />
        <a href="tel:5103380667">(510) 338-0667</a> ·{' '}
        <a href="mailto:info@montclairvitamins.com">info@montclairvitamins.com</a>
      </p>
    </PolicyLayout>
  );
}

/* ─────────────────────────────────────────
   REFUND / CANCELLATION POLICY
───────────────────────────────────────── */
export function RefundPolicyPage() {
  return (
    <PolicyLayout title="Refund & Cancellation Policy" lastUpdated="February 2026">
      <p>
        We want you to feel confident booking with Healthy Life Vitamins. Below is our complete
        policy for session cancellations, refunds, and membership changes.
      </p>

      <h2>1. Session Cancellations</h2>
      <h3>24-Hour Cancellation Policy</h3>
      <p>
        To cancel or reschedule a session without penalty, please notify us at least{' '}
        <strong>24 hours before your scheduled appointment</strong> by calling{' '}
        <a href="tel:5103380667">(510) 338-0667</a> or emailing{' '}
        <a href="mailto:info@montclairvitamins.com">info@montclairvitamins.com</a>.
      </p>
      <ul>
        <li><strong>Cancelled with 24+ hours notice:</strong> Session credit is returned to your account for future use.</li>
        <li><strong>Cancelled with less than 24 hours notice:</strong> The session is forfeited and cannot be refunded or rescheduled.</li>
        <li><strong>No-show:</strong> Treated as a late cancellation; session is forfeited.</li>
      </ul>

      <h2>2. Single Session Refunds ($69)</h2>
      <ul>
        <li>Refunds are available if cancellation is requested <strong>more than 24 hours</strong> before the scheduled session.</li>
        <li>Refunds are processed to the original payment method within <strong>5–10 business days</strong>.</li>
        <li>No refunds for completed sessions.</li>
      </ul>

      <h2>3. 2-Session Pack Refunds ($125)</h2>
      <ul>
        <li>Unused sessions may be refunded within <strong>30 days of purchase</strong> if no sessions have been used.</li>
        <li>If one session has been used, a partial refund of <strong>$56</strong> (single session rate of $69 deducted) is available for the unused session within the 30-day validity window.</li>
        <li>No refunds after the 30-day validity window expires.</li>
      </ul>

      <h2>4. Monthly Membership Cancellations ($220/month)</h2>
      <ul>
        <li>You may cancel your membership at any time by contacting us or through your account at{' '}
          <a href="/account">/account</a>.</li>
        <li>Cancellation takes effect at the <strong>end of the current billing period</strong>. You retain access to all sessions and perks through the end of the paid month.</li>
        <li>No partial-month refunds are issued for early cancellation.</li>
        <li>Sessions from a cancelled membership period do not carry over.</li>
      </ul>

      <h2>5. Wellness Club Perks</h2>
      <p>
        Wellness Club Perks (5% cashback, 10% Wednesday discount, personalized vitamin plan,
        pre-order privileges) are only active during a paid membership period. Perks expire
        immediately upon membership cancellation and cannot be transferred or refunded.
      </p>

      <h2>6. Equipment or Service Issues</h2>
      <p>
        If a session is cut short or cannot be completed due to equipment malfunction or a
        staff-initiated cancellation, you will receive a <strong>full session credit</strong>{' '}
        returned to your account, or a refund at your request.
      </p>

      <h2>7. How to Request a Refund</h2>
      <p>To initiate a cancellation or refund request:</p>
      <ul>
        <li>Call us at <a href="tel:5103380667">(510) 338-0667</a> during store hours.</li>
        <li>Email <a href="mailto:info@montclairvitamins.com">info@montclairvitamins.com</a> with your name, booking date, and reason.</li>
        <li>Visit us in-store at 6130 Medau Pl, Oakland, CA 94611.</li>
      </ul>
      <p>
        <strong>Store Hours:</strong> Monday–Saturday 10AM–6PM · Sunday 11AM–5PM
      </p>

      <h2>8. Contact</h2>
      <p>
        Healthy Life Vitamins · 6130 Medau Pl, Oakland, CA 94611<br />
        <a href="tel:5103380667">(510) 338-0667</a> ·{' '}
        <a href="mailto:info@montclairvitamins.com">info@montclairvitamins.com</a>
      </p>
    </PolicyLayout>
  );
}
